import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Dialog } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import {
  DialogCloseButton as CloseButton, DialogFooter as Footer,
  DialogHeader as Header, DialogTitle as Title,
} from '../common/dialog/primitives';
import { TEXT_DARK } from '../common/tokens';
import CreateListForm from './CreateListForm';
import ListRow from './ListRow';
import SearchField from './SearchField';
import useListPicker from './useListPicker';
import {
  DialogPaper, DoneButton, EmptyState, FooterText, RowList,
  SectionHint, SectionLabel, SectionLabelText, ShowMoreButton, ShowMoreWrap,
} from './styles';

function AddItemToListDialog(props) {
  const {
    open, onClose, item, itemIds, onMembershipChange,
  } = props;

  const searchInputRef = useRef(null);
  const newListInputRef = useRef(null);

  const picker = useListPicker({
    open, item, itemIds, onMembershipChange,
  });

  let body;
  if (picker.initialLoading || picker.searchInitialLoading) {
    body = (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '32px 0' }}>
        <CircularProgress size={20} thickness={4} sx={{ color: TEXT_DARK }} />
      </Box>
    );
  } else if (!picker.visibleLists.length) {
    body = (
      <EmptyState>
        {picker.searching
          ? `No lists match “${picker.debouncedQuery}”.`
          : 'You have no lists yet.'}
      </EmptyState>
    );
  } else {
    body = picker.visibleLists.map((listInfo) => (
      <ListRow
        key={listInfo.id}
        listInfo={listInfo}
        checked={picker.added.has(listInfo.id)}
        onToggle={picker.toggleList}
      />
    ));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperComponent={DialogPaper}
      PaperProps={{ elevation: 0 }}
      aria-labelledby="add-to-list-title"
      TransitionProps={{
        onEntered: () => searchInputRef.current?.focus(),
      }}
    >
      <Header>
        <Title id="add-to-list-title">
          {picker.bulk
            ? `Add ${picker.bulkCount} ${picker.bulkCount === 1 ? 'item' : 'items'} to list`
            : 'Add to list'}
        </Title>
        <CloseButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </CloseButton>
      </Header>

      <SearchField
        inputRef={searchInputRef}
        value={picker.query}
        onChange={picker.setQuery}
      />

      <CreateListForm
        open={picker.showCreate}
        name={picker.newListName}
        creating={picker.creating}
        inputRef={newListInputRef}
        onOpen={picker.openCreate}
        onNameChange={picker.setNewListName}
        onSubmit={picker.handleCreate}
        onCancel={picker.cancelCreate}
      />

      <SectionLabel>
        <SectionLabelText>{picker.searching ? 'Results' : 'Recent'}</SectionLabelText>
        <SectionHint>
          {picker.searching
            ? `${picker.visibleLists.length} match${picker.visibleLists.length === 1 ? '' : 'es'}`
            : `${picker.visibleLists.length} loaded`}
        </SectionHint>
      </SectionLabel>

      <RowList aria-busy={picker.loadingRecent || picker.loadingMore || picker.loadingSearch}>
        {body}
      </RowList>

      {!picker.searching && picker.hasMore && !picker.initialLoading && (
        <ShowMoreWrap>
          <ShowMoreButton
            variant="outlined"
            onClick={picker.loadMore}
            disabled={picker.loadingMore}
            aria-busy={picker.loadingMore}
          >
            {picker.loadingMore ? (
              <>
                <CircularProgress size={13} thickness={4} sx={{ color: TEXT_DARK }} />
                Loading…
              </>
            ) : (
              <>
                Show more
                <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
              </>
            )}
          </ShowMoreButton>
        </ShowMoreWrap>
      )}

      <Footer sx={{ justifyContent: 'space-between' }}>
        <FooterText>
          {picker.bulk
            ? `${picker.toggleCount} change${picker.toggleCount === 1 ? '' : 's'}`
            : `${picker.toggleCount} selected`}
        </FooterText>
        <DoneButton
          variant="contained"
          disableElevation
          onClick={onClose}
        >
          Done
        </DoneButton>
      </Footer>
    </Dialog>
  );
}

AddItemToListDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    id: PropTypes.string,
    privateData: PropTypes.shape({ lists: PropTypes.array }),
    publicData: PropTypes.shape({ lists: PropTypes.array }),
  }),
  itemIds: PropTypes.arrayOf(PropTypes.string),
  onMembershipChange: PropTypes.func,
};

AddItemToListDialog.defaultProps = {
  open: false,
  item: null,
  itemIds: null,
  onMembershipChange: null,
};

export default AddItemToListDialog;
